import React, { useState } from 'react';
import { NavLink, useLocation, useParams } from 'react-router-dom';
import { Home, HelpCircle, User, UserPlus, LogIn, X, Hash, Brain, Tag, Quote, BookOpen } from 'lucide-react';
import { HELP_CONTENT } from '../constants/HelpContent';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../context/AuthContext';

const BottomNavBar: React.FC = () => {
  const [showHelp, setShowHelp] = useState(false);
  const location = useLocation();
  const params = useParams();
  const { user } = useAuth();  // Guest Features Phase 3

  // Determine screen type based on route
  const getScreenType = (): string => {
    const path = location.pathname;

    if (path === '/') return 'home';
    if (path === '/login' || path === '/register') return 'login';
    if (path === '/profile') return 'profile';
    if (path === '/random-wisdom') return 'random_wisdom';  // Guest Features Phase 5

    // Collection tables
    if (path === '/topics') return 'topics_table';
    if (path === '/thoughts') return 'thoughts_table';
    if (path === '/quotes') return 'quotes_table';
    if (path === '/passages') return 'passages_table';
    if (path === '/tags') return 'tags_table';

    // Personalization pages
    if (path === '/favorites') return 'favorites';
    if (path === '/notes') return 'notes';

    // Detail pages - Support both singular and plural paths (e.g. /topic/49 or /topics/49)
    if (path.match(/\/(topics?)\/[^/]+/)) return 'topic_detail';
    if (path.match(/\/(thoughts?)\/[^/]+/)) return 'thought_detail';
    if (path.match(/\/(quotes?)\/[^/]+/)) return 'quote_detail';
    if (path.match(/\/(passages?)\/[^/]+/)) return 'passage_detail';
    if (path.match(/\/(tags?)\/[^/]+/)) return 'tag_detail';

    return 'default';
  };

  const screenType = getScreenType();
  const helpContent = HELP_CONTENT[screenType] || HELP_CONTENT.home || {
    title: "Wisdom Help",
    content: "# Help Not Found\n\nNo specific help content found for this section. Please return to the Home screen for general assistance."
  };

  // Calculate responsive max-width for Help Panel based on screen type
  const getHelpPanelMaxWidth = (): string => {
    switch (screenType) {
      case 'home':
        return 'max-w-full sm:max-w-2xl md:max-w-3xl lg:max-w-4xl';
      case 'profile':
        return 'max-w-full sm:max-w-xl md:max-w-2xl';
      case 'login':
        return 'max-w-full sm:max-w-md';
      case 'random_wisdom':
        return 'max-w-full sm:max-w-2xl md:max-w-3xl';
      default:
        // Table/detail pages
        return 'max-w-full sm:max-w-3xl md:max-w-4xl lg:max-w-5xl xl:max-w-[1400px]';
    }
  };

  return (
    <>
      <nav className="fixed bottom-0 left-0 right-0 z-[70] bg-white border-t-[6px] border-brand-maroon h-16 sm:h-18 md:h-20 lg:h-20 xl:h-22">
        <div className="flex justify-around items-center h-full">
          {/* Home */}
          <NavLink
            to="/"
            className={({ isActive }) =>
              `flex flex-col items-center justify-center flex-1 h-full gap-1 ${isActive ? 'text-[#673147]' : 'text-[#673147]/50'
              }`
            }
          >
            <Home size={26} className="sm:w-7 sm:h-7 md:w-8 md:h-8 lg:w-9 lg:h-9" />
            <span className="text-[11px] sm:text-xs md:text-sm lg:text-base font-semibold">Home</span>
          </NavLink>

          {/* Topics (Conditional) */}
          {screenType === 'topic_detail' && (
            <>
              {/* Divider */}
              <div className="w-px h-[40%] bg-[#673147]/10" />

              <NavLink
                to="/topics"
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center flex-1 h-full gap-1 ${isActive ? 'text-[#673147]' : 'text-[#673147]/50'
                  }`
                }
              >
                <Hash size={26} className="sm:w-7 sm:h-7 md:w-8 md:h-8 lg:w-9 lg:h-9" />
                <span className="text-[11px] sm:text-xs md:text-sm lg:text-base font-semibold">Topics</span>
              </NavLink>
            </>
          )}

          {/* Thoughts (Conditional) */}
          {screenType === 'thought_detail' && (
            <>
              {/* Divider */}
              <div className="w-px h-[40%] bg-[#673147]/10" />

              <NavLink
                to="/thoughts"
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center flex-1 h-full gap-1 ${isActive ? 'text-[#673147]' : 'text-[#673147]/50'
                  }`
                }
              >
                <Brain size={26} className="sm:w-7 sm:h-7 md:w-8 md:h-8 lg:w-9 lg:h-9" />
                <span className="text-[11px] sm:text-xs md:text-sm lg:text-base font-semibold">Thoughts</span>
              </NavLink>
            </>
          )}

          {/* Quotes (Conditional) */}
          {screenType === 'quote_detail' && (
            <>
              {/* Divider */}
              <div className="w-px h-[40%] bg-[#673147]/10" />

              <NavLink
                to="/quotes"
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center flex-1 h-full gap-1 ${isActive ? 'text-[#673147]' : 'text-[#673147]/50'
                  }`
                }
              >
                <Quote size={26} className="sm:w-7 sm:h-7 md:w-8 md:h-8 lg:w-9 lg:h-9" />
                <span className="text-[11px] sm:text-xs md:text-sm lg:text-base font-semibold">Quotes</span>
              </NavLink>
            </>
          )}

          {/* Passages (Conditional) */}
          {screenType === 'passage_detail' && (
            <>
              {/* Divider */}
              <div className="w-px h-[40%] bg-[#673147]/10" />

              <NavLink
                to="/passages"
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center flex-1 h-full gap-1 ${isActive ? 'text-[#673147]' : 'text-[#673147]/50'
                  }`
                }
              >
                <BookOpen size={26} className="sm:w-7 sm:h-7 md:w-8 md:h-8 lg:w-9 lg:h-9" />
                <span className="text-[11px] sm:text-xs md:text-sm lg:text-base font-semibold">Bible</span>
              </NavLink>
            </>
          )}

          {/* Tags (Conditional) */}
          {screenType === 'tag_detail' && (
            <>
              {/* Divider */}
              <div className="w-px h-[40%] bg-[#673147]/10" />

              <NavLink
                to="/tags"
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center flex-1 h-full gap-1 ${isActive ? 'text-[#673147]' : 'text-[#673147]/50'
                  }`
                }
              >
                <Tag size={26} className="sm:w-7 sm:h-7 md:w-8 md:h-8 lg:w-9 lg:h-9" />
                <span className="text-[11px] sm:text-xs md:text-sm lg:text-base font-semibold">Tags</span>
              </NavLink>
            </>
          )}

          {/* Divider */}
          <div className="w-px h-[40%] bg-[#673147]/10" />

          {/* Give $ - Ko-fi Support */}
          <a
            href="https://ko-fi.com/wisdombook"
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center justify-center flex-1 h-full gap-1 text-brand-maroon/50 hover:text-brand-maroon transition-colors"
          >
            <span className="text-2xl sm:text-3xl md:text-4xl lg:text-[2.5rem]">☕</span>
            <span className="text-[11px] sm:text-xs md:text-sm lg:text-base font-semibold">Tip $</span>
          </a>

          {/* Divider */}
          <div className="w-px h-[40%] bg-[#673147]/10" />

          {/* Help */}
          <button
            onClick={() => setShowHelp(true)}
            disabled={location.pathname === '/starthere'}
            className={`flex flex-col items-center justify-center flex-1 h-full gap-1 ${location.pathname === '/starthere' ? 'opacity-30 cursor-not-allowed text-[#673147]/50' : 'text-brand-maroon/50'}`}
          >
            <HelpCircle size={26} className="sm:w-7 sm:h-7 md:w-8 md:h-8 lg:w-9 lg:h-9" />
            <span className="text-[11px] sm:text-xs md:text-sm lg:text-base font-semibold">Help</span>
          </button>

          {/* Divider */}
          <div className="w-px h-[40%] bg-[#673147]/10" />

          {/* Guest Features Phase 3: Join/Login icons (guests) or Profile icon (authenticated) - Hidden on register/login pages */}
          {screenType !== 'login' && (
            user ? (
              // Authenticated: Show Profile icon
              <NavLink
                to="/profile"
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center flex-1 h-full gap-1 ${isActive ? 'text-[#673147]' : 'text-[#673147]/50'
                  }`
                }
              >
                <User size={26} className="sm:w-7 sm:h-7 md:w-8 md:h-8 lg:w-9 lg:h-9" />
                <span className="text-[11px] sm:text-xs md:text-sm lg:text-base font-semibold">Profile</span>
              </NavLink>
            ) : (
              // Guest: Show both Join and Login icons
              <>
                <NavLink
                  to="/register"
                  className={({ isActive }) =>
                    `flex flex-col items-center justify-center flex-1 h-full gap-1 ${isActive ? 'text-[#673147]' : 'text-[#673147]/50'
                    }`
                  }
                >
                  <UserPlus size={26} className="sm:w-7 sm:h-7 md:w-8 md:h-8 lg:w-9 lg:h-9" />
                  <span className="text-[11px] sm:text-xs md:text-sm lg:text-base font-semibold">Join</span>
                </NavLink>

                {/* Divider */}
                <div className="w-px h-[40%] bg-[#673147]/10" />

                <NavLink
                  to="/login"
                  className={({ isActive }) =>
                    `flex flex-col items-center justify-center flex-1 h-full gap-1 ${isActive ? 'text-[#673147]' : 'text-[#673147]/50'
                    }`
                  }
                >
                  <LogIn size={26} className="sm:w-7 sm:h-7 md:w-8 md:h-8 lg:w-9 lg:h-9" />
                  <span className="text-[11px] sm:text-xs md:text-sm lg:text-base font-semibold">Login</span>
                </NavLink>
              </>
            )
          )}
        </div>
      </nav>

      {/* Help Modal */}
      {showHelp && (
        <div
          className="fixed inset-0 bg-black/50 z-[60] flex flex-col justify-end"
          onClick={() => setShowHelp(false)}
        >
          <div
            className={`mx-auto bg-papyrus rounded-t-3xl max-h-[62vh] sm:max-h-[55vh] ipad:max-h-[75vh] lg:max-h-[70vh] overflow-hidden flex flex-col ${getHelpPanelMaxWidth()}`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex justify-between items-center py-3 px-4 border-b border-brand-maroon shrink-0 bg-brand-maroon">
              <h2 className="text-xl font-bold text-white leading-none m-0">{helpContent.title}</h2>
              <button
                onClick={() => setShowHelp(false)}
                className="text-white/70 hover:text-white transition-colors flex items-center"
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 pb-24 overflow-y-auto flex-1 font-serif">
              <div className="space-y-4 text-black whitespace-pre-line">
                <ReactMarkdown
                  components={{
                    h1: (props: any) => <h1 className="text-3xl font-bold text-brand-maroon mb-4" {...props} />,
                    h2: (props: any) => <h2 className="text-2xl font-bold text-brand-maroon mt-6 mb-3" {...props} />,
                    h3: (props: any) => <h3 className="text-xl font-bold text-brand-maroon mt-4 mb-2" {...props} />,
                    p: (props: any) => <p className="mb-3 leading-relaxed text-black font-medium text-lg" {...props} />,
                    ul: (props: any) => <ul className="list-disc pl-5 mb-4 space-y-1 text-lg" {...props} />,
                    li: (props: any) => <li className="pl-1" {...props} />,
                    strong: (props: any) => <strong className="font-semibold text-brand-maroon" {...props} />,
                  }}
                >
                  {helpContent.content}
                </ReactMarkdown>

                {/* End of content separator */}
                <div className="flex justify-center items-center py-8 text-brand-maroon/40 text-3xl font-bold select-none">
                  ~ ❦ ~
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BottomNavBar;
